/**
 * Parsed Azure error information
 */
export interface ParsedAzureError {
  isAzureError: boolean;
  exceptionType?: string;
  status?: number;
  statusText?: string;
  errorCode?: string;
  message?: string;
  originalMessage: string;
}

/**
 * Parses Azure error messages to extract structured information.
 *
 * Looks for patterns like:
 * - Azure.RequestFailedException: Service request failed.
 *   Status: 404 (The specified blob does not exist.)
 *   ErrorCode: BlobNotFound
 *
 * @param error - The error object or message to parse
 * @returns ParsedAzureError with extracted information
 */
export const parseAzureError = (error: unknown): ParsedAzureError => {
  const originalMessage =
    error instanceof Error ? error.message : String(error);

  // Check if this looks like an Azure error
  const azurePattern = /Azure\.(\w+Exception):\s*(.+?)(?:\n|$)/i;
  const statusPattern = /Status:\s*(\d+)\s*\(([^)]+)\)/i;
  const errorCodePattern = /ErrorCode:\s*(\w+)/i;

  const azureMatch = originalMessage.match(azurePattern);
  const statusMatch = originalMessage.match(statusPattern);
  const errorCodeMatch = originalMessage.match(errorCodePattern);

  if (!azureMatch && !statusMatch && !errorCodeMatch) {
    return {
      isAzureError: false,
      originalMessage,
    };
  }

  const exceptionType = azureMatch?.[1];
  const message = azureMatch?.[2]?.trim();
  const status = statusMatch?.[1] ? parseInt(statusMatch[1], 10) : undefined;
  const statusText = statusMatch?.[2]?.trim();
  const errorCode = errorCodeMatch?.[1];

  return {
    isAzureError: true,
    exceptionType,
    status,
    statusText,
    errorCode,
    message,
    originalMessage,
  };
};

/**
 * Formats an Azure error into a user-friendly message
 */
export const formatAzureError = (parsed: ParsedAzureError): string => {
  if (!parsed.isAzureError) {
    return parsed.originalMessage;
  }

  const parts: string[] = [];

  if (parsed.errorCode) {
    parts.push(`Error: ${parsed.errorCode}`);
  }

  if (parsed.status && parsed.statusText) {
    parts.push(`Status: ${parsed.status} - ${parsed.statusText}`);
  } else if (parsed.status) {
    parts.push(`Status: ${parsed.status}`);
  }

  if (parsed.message) {
    parts.push(parsed.message);
  }

  return parts.length > 0 ? parts.join(' | ') : parsed.originalMessage;
};
